import { UserPreferences, FarmingPackage, PackageItem } from "./types";
import { PRODUCTS, CONSTANTS } from "./farm-data";

export function generateRecommendations(prefs: UserPreferences): FarmingPackage[] {
  const filteredProducts = PRODUCTS.filter((p) => p.category === prefs.livestockType);
  const animal = filteredProducts.find((p) => p.subcategory === "animal");
  const feed = filteredProducts.find((p) => p.subcategory === "feed");
  const equip = filteredProducts.find((p) => p.subcategory === "equipment");
  const med = filteredProducts.find((p) => p.subcategory === "medication");
  const training = PRODUCTS.find(
    (p) => p.category === prefs.livestockType && p.subcategory === "training"
  );
  if (!animal || !feed || !equip) return [];

  const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

  const createPackage = (
    type: "starter" | "recommended" | "pro",
    multiplier: number,
    name: string
  ): FarmingPackage | null => {
    const budgetCap = prefs.budget * multiplier;
    const animalBudget = budgetCap * 0.45;
    let animalCount = Math.floor(animalBudget / animal.price);

    if (animalCount < 1) {
      if (type === "starter") animalCount = 1;
      else return null;
    }

    const feedCount = Math.max(1, Math.ceil(animalCount * 0.15));
    const equipCount = Math.max(1, Math.floor(animalCount / 25));
    const items: PackageItem[] = [
      { product: animal, quantity: animalCount, subtotal: animalCount * animal.price },
      { product: feed, quantity: feedCount, subtotal: feedCount * feed.price, frequency: "monthly" },
      { product: equip, quantity: equipCount, subtotal: equipCount * equip.price },
    ];

    if (med) {
      items.push({ product: med, quantity: 1, subtotal: med.price, frequency: "monthly" });
    }

    if (prefs.includeTrainingUpsell && training) {
      items.push({ product: training, quantity: 1, subtotal: training.price });
    }

    const itemSubtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
    const markup = itemSubtotal * (CONSTANTS.MARKUP_RATE - 1);
    const deliveryFee = (CONSTANTS.ZONE_FEES as Record<string, number>)[prefs.location] || 35;
    const totalCost = itemSubtotal + markup + deliveryFee;

    const currentMonth = new Date().getMonth();
    const seasonKey = currentMonth < 3 ? "Q1" : currentMonth < 6 ? "Q2" : currentMonth < 9 ? "Q3" : "Q4";
    const seasonalFactor = CONSTANTS.SEASONAL_FACTORS[seasonKey] || 1.0;
    const expModifier = prefs.experience === "expert" ? 1.2 : prefs.experience === "intermediate" ? 1.05 : 0.9;
    const baseMargin = (CONSTANTS.ROI_ESTIMATES[prefs.livestockType] || 0.25) / seasonalFactor;
    const margin = baseMargin * expModifier;
    const annualRevenue = totalCost * (1 + margin) * 2;
    const fixedOverhead = 50;
    const annualProfit = Math.max(0, annualRevenue - totalCost * 2 - fixedOverhead);

    const monthlyNet = annualProfit / 12;
    const breakEvenMonth = monthlyNet > 0 ? Math.ceil(totalCost / monthlyNet) : 24;

    const baseConfidenceByType = { starter: 72, recommended: 84, pro: 76 };
    const baseConfidence = baseConfidenceByType[type];
    const expFitBonus =
      (prefs.experience === "beginner" && type === "starter") ||
      (prefs.experience === "intermediate" && type === "recommended") ||
      (prefs.experience === "expert" && type === "pro")
        ? 5
        : -2;
    const budgetFitBonus = totalCost <= prefs.budget ? 4 : -8;
    const trainingBonus = prefs.includeTrainingUpsell ? 2 : 0;
    const confidenceScore = Math.round(
      clamp(baseConfidence + expFitBonus + budgetFitBonus + trainingBonus, 50, 95)
    );

    const generateExplanation = (): string => {
      const pieces: string[] = [];
      
      // Experience fit explanation
      if (type === "starter" && prefs.experience === "beginner") {
        pieces.push("This starter package matches your beginner experience level with manageable daily tasks and lower initial costs.");
      } else if (type === "recommended" && prefs.experience === "intermediate") {
        pieces.push("This package balances profitability with your intermediate experience—you have the skills to manage higher volumes.");
      } else if (type === "pro" && prefs.experience === "expert") {
        pieces.push("This professional setup leverages your expertise to maximize production and profit potential.");
      } else {
        pieces.push(`This ${type} package respects your ${prefs.experience} experience level while optimizing for controllable risk.`);
      }

      // Budget alignment
      if (totalCost <= prefs.budget) {
        const remaining = prefs.budget - totalCost;
        pieces.push(`At ${remaining > 0 ? `₦${remaining.toLocaleString()}` : "your exact budget"}, you stay within your ₦${prefs.budget.toLocaleString()} limit.`);
      } else {
        const over = totalCost - prefs.budget;
        pieces.push(`This stretches ₦${over.toLocaleString()} beyond your stated budget—consider scaling down quantities.`);
      }

      // Goal alignment
      const goalText: Record<string, string> = {
        "family-consumption": "designed for family needs with seasonal surpluses",
        "side-income": "structured for part-time income during weekends or spare hours",
        "primary-income": "built to be your main livelihood with daily operational demands",
        "commercial": "sized for bulk sales and wholesale distribution",
      };
      pieces.push(`Items are ${goalText[prefs.goal]}.`);

      // Space and time availability
      const spaceNote =
        prefs.spaceSqM < 100
          ? "Your limited space is fit for intensive systems."
          : prefs.spaceSqM < 500
          ? "Your space allows moderate density—balanced care and profit."
          : "Your larger space lets you spread animals, reducing disease risk.";
      pieces.push(spaceNote);

      if (prefs.timeAvailability === "weekends-only") {
        pieces.push("This plan assumes minimal daily attention—feed and water are prioritized; less frequent monitoring needed.");
      } else if (prefs.timeAvailability === "daily-attention") {
        pieces.push("This plan includes daily check-in tasks: feeding, health inspections, and record-keeping.");
      } else if (prefs.timeAvailability === "full-time") {
        pieces.push("You can dedicate full-time hours, so this plan includes more frequent tasks and higher-touch management.");
      }

      // Training integration
      if (prefs.includeTrainingUpsell) {
        pieces.push("Training is included to help you execute the plan correctly from day one.");
      }

      // Break-even timeline
      if (breakEvenMonth <= 6) {
        pieces.push(`Break-even is expected in ${breakEvenMonth} months, giving you confidence in the investment.`);
      } else if (breakEvenMonth <= 12) {
        pieces.push(`You'll recover costs within ${breakEvenMonth} months, generating profit by year-end.`);
      } else {
        pieces.push(`Recovery takes ${breakEvenMonth} months—typical for livestock, but requires sustained effort.`);
      }

      return pieces.join(" ");
    };

    return {
      id: `pkg-${type}-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
      name,
      type,
      items,
      totalCost,
      deliveryFee,
      projectedAnnualRevenue: annualRevenue,
      projectedAnnualProfit: annualProfit,
      roiPercentage: Math.round(margin * 100),
      confidenceScore,
      breakEvenMonth,
      description: `${name} optimized for ${prefs.location} (${seasonKey} Season).`,
      explanationText: generateExplanation(),
    };
  };

  return [
    createPackage("starter", 0.5, "Small Scale Start"),
    createPackage("recommended", 0.9, "Optimal Value"),
    createPackage("pro", 1.4, "Commercial Expansion"),
  ].filter((p): p is FarmingPackage => p !== null && p.totalCost <= prefs.budget * 2.2);
}